import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

import { postAuthDestination, sanitizeNextPath, storeNext } from './next';

import { profileQueries } from '@/lib/queries/profile';
import { supabase } from '@/lib/supabase';

/**
 * The single post-auth landing decision, shared by password sign-in and the
 * callback. Sanitizes the raw next, reads the fresh profile, then applies the
 * precedence chain (suspended ▸ onboarding-carry ▸ next ▸ default) and
 * navigates. Onboarding-bound users have their next stashed for the completion
 * CTA to consume.
 */
export function useLandAfterAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useCallback(
    async (rawNext: string | null | undefined, opts?: { recovery?: boolean }): Promise<void> => {
      const next = sanitizeNextPath(rawNext);
      const { data } = await supabase.auth.getSession();
      const userId = data.session?.user.id;
      const profile = userId
        ? await queryClient.fetchQuery(profileQueries.byId(userId)).catch(() => null)
        : null;
      const { to, carryNext } = postAuthDestination({
        profile,
        next,
        recovery: opts?.recovery ?? false,
      });
      if (carryNext) storeNext(next);
      void navigate({ to });
    },
    [navigate, queryClient],
  );
}
